import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { last } from 'GeminiViewerComponent/helpers/lodashUtils';
import { convertNodeTypes } from 'GeminiViewerComponent/helpers/node_helpers';
import { v4 as uuidv4 } from 'uuid';
import { ProcedureNodeTypes } from '../ProcedureNodeTypes';

let currentDate = new Date();

const offset = currentDate.getTimezoneOffset();
currentDate = new Date(currentDate.getTime() - offset * 60 * 1000);

const initialState = {
    trees: {},
    active_tree_id: null,
    loadingStatus: LoadingStatus.Idle,
    error: '',
    displaySessionDialog: true,
    isBranchProcedure: false,
    hotspotRetryCount: 0,
};

export const findNextNode = (sortedNodes, node) => {
    const index = sortedNodes.findIndex((item) => item.id === node.id);
    if (index !== -1 && index < sortedNodes.length - 1) {
        return sortedNodes[index + 1];
    } else {
        return null;
    }
};

export const sortNodes = (nodes) => {
    const nodeValues = Object.keys(nodes ?? {}).map((key) => {
        return { id: key, ...nodes[key] };
    });
    const sortedNodes = nodeValues.sort(function (a, b) {
        return a.order_idx - b.order_idx;
    });
    return sortedNodes;
};

export const getAllProcedureVariables = (nodes) => {
    if (!Array.isArray(nodes) || nodes.length === 0) {
        return [];
    }
    const findVariables = [];

    function addFieldVariable(field) {
        if (field.variable) {
            findVariables.push({
                id: field.id,
                name: field.variable,
            });
        }
        if (field.type === 'initial' && field.date_variable) {
            findVariables.push({
                id: field.id,
                name: field.date_variable,
            });
        }
    }

    for (const node of nodes) {
        if (node.type !== ProcedureNodeTypes.content.type) {
            continue;
        }
        if (node.variable) {
            findVariables.push({ id: node.id, name: node.variable });
        }

        node.fields?.forEach((field) => {
            if (field.type === 'group' || field.type === 'table') {
                field.fields?.forEach((child) => addFieldVariable(child));
            } else {
                addFieldVariable(field);
            }
        });
    }
    return findVariables;
};

export const getUniqueVariableName = (name, allProcedureVariableNames) => {
    let suffix = 2;
    const suffixIndex = name.lastIndexOf('_');
    while (allProcedureVariableNames.includes(name)) {
        name = `${suffixIndex < 0 ? name : name.substring(0, suffixIndex)}_${suffix++}`;
    }
    allProcedureVariableNames.push(name);
    return name;
};

export const copyField = (field, allProcedureVariableNames) => {
    const copy = { ...field, id: uuidv4() };
    if (copy.type === 'group' || copy.type === 'table') {
        if (Array.isArray(copy.fields)) {
            copy.fields = copy.fields.map((child) => copyField(child, allProcedureVariableNames));
        }
    } else {
        if (copy.variable) {
            copy.variable = getUniqueVariableName(copy.variable, allProcedureVariableNames);
        }

        if (copy.type === 'initial' && copy.date_variable) {
            copy.date_variable = getUniqueVariableName(copy.date_variable, allProcedureVariableNames);
        }
    }
    return copy;
};

export const setHeaderVariables = async (procedure) => {
    let updatedProcedure = procedure;
    if (procedure?.header_variables && procedure?.nodes) {
        let findVariables = getAllProcedureVariables(procedure?.nodes);
        const headerVars = await procedure?.header_variables?.map((headerVar) => {
            return {
                id: headerVar.id,
                name: findVariables?.find((hdVar) => hdVar?.id === headerVar.id)?.name,
            };
        });
        updatedProcedure = { ...procedure, header_variables: headerVars };
    }
    return updatedProcedure;
};

const getFieldAnswers = (options) => {
    if (options) {
        return options
            ?.map((btn) => {
                return { label: btn?.answer_value };
            })
            ?.filter((btn) => btn?.label);
    }
    return [];
};

const getFormFieldAnswers = (field) => {
    let fieldObj = {
        name: field.variable,
        label: field.variable,
    };
    if (field) {
        let opts;
        switch (field.type) {
            case 'multiline':
                return { ...fieldObj, valueEditorType: 'textarea' };
            case 'checkbox':
                return {
                    ...fieldObj,
                    valueEditorType: 'checkbox',
                    defaultValue: opts?.[0]?.label,
                    operators: [{ name: '=', label: '=' }],
                };
            case 'select':
                opts = field.options
                    ?.map((opt) => {
                        return { label: opt?.value };
                    })
                    ?.filter((btn) => btn?.label);
                return {
                    ...fieldObj,
                    valueEditorType: 'select',
                    defaultValue: opts?.[0]?.label,
                    values: opts,
                    operators: [
                        { name: '=', label: '=' },
                        { name: '!=', label: '!=' },
                    ],
                };
            case 'radiogroup':
                opts = field.fields
                    ?.map((opt) => {
                        return { name: opt?.label, label: opt?.label };
                    })
                    ?.filter((btn) => btn?.label);
                return {
                    ...fieldObj,
                    valueEditorType: 'radio',
                    defaultValue: opts?.[0]?.label,
                    values: opts,
                    operators: [
                        { name: '=', label: '=' },
                        { name: '!=', label: '!=' },
                    ],
                };
            case 'datetime':
                opts = field.fields
                    ?.map((opt) => {
                        return { label: opt?.label };
                    })
                    ?.filter((btn) => btn?.label);
                return {
                    ...fieldObj,
                    inputType:
                        field.date_time_type === 'datetime'
                            ? 'datetime-local'
                            : field.date_time_type === 'time'
                              ? 'time'
                              : 'date',
                    values: opts,
                };
            default:
                return { ...fieldObj };
        }
    }
    return fieldObj;
};

export const getQueryBuilderFieldsData = async (nodes) => {
    let findVariables = [];
    let fieldObj = {};
    await nodes?.map((nd) => {
        if (nd.type === ProcedureNodeTypes.content.type) {
            if (nd?.variable) {
                fieldObj = {
                    name: nd.variable,
                    label: nd.variable,
                    valueEditorType: 'select',
                    defaultValue: '',
                    operators: [
                        { name: '=', label: '=' },
                        { name: '!=', label: '!=' },
                    ],
                };
                if (nd?.answer_group === 'image') {
                    let imageOpts = getFieldAnswers(nd?.image_options);
                    if (
                        nd?.multiple_answers_allow === true &&
                        Array.isArray(nd?.image_target_combinations) &&
                        nd?.image_target_combinations?.length > 0
                    ) {
                        imageOpts = [...imageOpts, ...getFieldAnswers(nd?.image_target_combinations)];
                    }
                    if (Array.isArray(imageOpts) && imageOpts?.length > 0) {
                        fieldObj = {
                            ...fieldObj,
                            defaultValue: imageOpts?.[0]?.label,
                            values: imageOpts,
                        };
                    }
                } else if (nd?.answer_group === 'select') {
                    let selectOpts = getFieldAnswers(nd?.select_options);
                    if (
                        nd?.multiple_answers_allow === true &&
                        Array.isArray(nd?.select_target_combinations) &&
                        nd?.select_target_combinations?.length > 0
                    ) {
                        selectOpts = [...selectOpts, ...getFieldAnswers(nd?.select_target_combinations)];
                    }
                    if (Array.isArray(selectOpts) && selectOpts?.length > 0) {
                        fieldObj = {
                            ...fieldObj,
                            defaultValue: selectOpts?.[0]?.label,
                            values: selectOpts,
                        };
                    }
                } else if (nd?.answer_group === 'hotspot') {
                    console.log('hotspot answer type');
                } else {
                    let buttonOpts = getFieldAnswers(nd?.option_buttons);
                    if (Array.isArray(buttonOpts) && buttonOpts?.length > 0) {
                        fieldObj = {
                            ...fieldObj,
                            defaultValue: buttonOpts?.[0]?.label,
                            values: buttonOpts,
                        };
                    }
                }
                findVariables.push(fieldObj);
            }
            nd?.fields?.map((field) => {
                if (field.type === 'group' || field.type === 'table') {
                    field?.fields?.map((fld) => {
                        if (fld?.variable) {
                            findVariables.push(getFormFieldAnswers(fld));
                        }
                        return false;
                    });
                } else {
                    if (field?.variable) {
                        findVariables.push(getFormFieldAnswers(field));
                    }
                }
                return false;
            });
        }
        return false;
    });
    findVariables = [
        ...findVariables,
        {
            name: 'allAnswersCorrect',
            label: 'All Answers Correct',
            valueEditorType: 'checkbox',
            defaultValue: false,
            operators: [{ name: '=', label: '=' }],
        },
        {
            name: 'correctAnswers',
            label: 'Correct Answers',
            inputType: 'number',
            operators: [
                { name: '=', label: '=' },
                { name: '!=', label: '!=' },
                { name: '<', label: '<' },
                { name: '>', label: '>' },
                { name: '<=', label: '<=' },
                { name: '>=', label: '>=' },
            ],
        },
        {
            name: 'percentOfCorrectAnswers',
            label: '% of Correct Answers',
            inputType: 'number',
            operators: [
                { name: '=', label: '=' },
                { name: '!=', label: '!=' },
                { name: '<', label: '<' },
                { name: '>', label: '>' },
                { name: '<=', label: '<=' },
                { name: '>=', label: '>=' },
            ],
        },
    ];
    return findVariables;
};

const setInitialNode = (tree, start_node_id = null) => {
    if (typeof start_node_id === 'number') {
        const startNode = tree.nodes.find((node) => +node.id === start_node_id);
        if (startNode) {
            tree.current_node = startNode;
            return;
        }
    }
    if (tree.parent_tree_id) {
        if (tree.start_node_id) {
            tree.current_node = tree.nodes.find((node) => Number(node.id) === Number(tree.start_node_id));
        } else if (tree.continue_node_id) {
            tree.current_node = tree.nodes.find((node) => Number(node.id) === Number(tree.continue_node_id));
        } else {
            tree.current_node = tree.nodes[0];
        }
    } else {
        if (tree.continue_node_id) {
            tree.current_node = tree.nodes.find((node) => Number(node.id) === Number(tree.continue_node_id));
        } else if (tree.start_node_id) {
            tree.current_node = tree.nodes.find((node) => Number(node.id) === Number(tree.start_node_id));
        } else {
            tree.current_node = tree.nodes[0];
        }
    }
};

const findNodeById = (tree, node_id) => {
    if (!tree?.allNodes) return null;
    return tree.allNodes.find((n) => Number(n.id) === Number(node_id)) ?? null;
};

export const fetchProcedure = createAsyncThunk('assets/fetchProcedure', async ({ service, procedureId }) => {
    return await service.getById(procedureId);
});

const handleNodeNavigation = (state, node, addToHistory = true) => {
    let activeTree = state.trees[state.active_tree_id];
    switch (node.type) {
        case ProcedureNodeTypes.branch.type:
            if (!node.branch_procedure?.procedure_id) {
                return;
            }
            // If this is a branch then load it now
            if (node.start_node_id) {
                node.branch_procedure = {
                    ...node.branch_procedure,
                    start_node_id: node.start_node_id,
                };
            }
            if (node.return_node_id) {
                node.branch_procedure = {
                    ...node.branch_procedure,
                    return_node_id: node.return_node_id,
                };
            }
            // if (node.end_node_id) {
            //     node.branch_procedure.end_node_id = node.end_node_id;
            // }
            state.trees[node.branch_procedure.procedure_id] = node.branch_procedure;
            activeTree = node.branch_procedure;
            activeTree.parent_tree_id = state.active_tree_id;
            state.active_tree_id = node.branch_procedure.procedure_id;
            activeTree.history = [];
            setInitialNode(activeTree);

            break;
        default:
            // If current node is a display type and next node is not, then
            // save current node as the last known display node. This allows
            // us to automatically navigate back to the last displayed node
            // in case the current flow ends on a non display type node.
            if (ProcedureNodeTypes[activeTree.current_node?.type]?.mode === 'display') {
                if (ProcedureNodeTypes[node.type].mode !== 'display') {
                    activeTree.lastDisplayNode = activeTree.current_node;
                } else {
                    activeTree.lastDisplayNode = null;
                }
            }
            if (activeTree.current_node && activeTree.current_node.id !== node.id) {
                if (activeTree.current_node?.type !== ProcedureNodeTypes.contentViewAction.type) {
                    if (addToHistory) {
                        activeTree.history.push(activeTree.current_node);
                    } else {
                        activeTree.history.pop();
                    }
                }
            }
            activeTree.current_node = node;
            break;
    }
};

const doGotoNode = (state, nodeId, addToHistory = true) => {
    const activeTree = state.trees[state.active_tree_id];
    const nodes = activeTree.allNodes ?? activeTree.nodes;
    const node = nodes.find((node) => node.id === nodeId) || activeTree.nodes.find((node) => node.id === nodeId);
    if (node) {
        state.isBranchProcedure = node.type === ProcedureNodeTypes.branch.type;
        handleNodeNavigation(state, node, addToHistory);
    }
};

const doNextNode = (state) => {
    const activeTree = state.trees[state.active_tree_id];
    const nextNode = findNextNode(activeTree.allNodes ?? activeTree.nodes ?? [], activeTree.current_node);
    if (nextNode) {
        handleNodeNavigation(state, nextNode);
    }
};

const skipSameNode = (state, nodes, last_node_id) => {
    if ([...nodes].pop().id === last_node_id) {
        nodes.pop();
        state.trees[state.active_tree_id].history = nodes;
        skipSameNode(state, nodes, last_node_id);
    }
    return nodes;
};

const skipHiddenNode = (state, nodes, last_node_id) => {
    if (!state.trees[state.active_tree_id]?.nodes.find((node) => node.id === last_node_id)) {
        const lastNode = nodes.pop();
        if (!lastNode) {
            // Prevent crash when there is no node to pop
            return { id: last_node_id };
        }
        state.trees[state.active_tree_id].history = nodes;
        skipHiddenNode(state, nodes, lastNode?.id);
        return lastNode;
    } else return { id: last_node_id };
};

const doPreviousNode = (state) => {
    const activeTree = state.trees[state.active_tree_id];
    if (activeTree.history.length > 0) {
        let lastNode = [...activeTree.history].pop();
        skipSameNode(state, [...activeTree.history], activeTree.current_node.id);
        lastNode = activeTree.history.pop();
        lastNode = skipHiddenNode(state, [...activeTree.history], lastNode?.id);
        // Since it could be possible to start with or land on a hidden node,
        // go ahead and search allNodes here - not just visible ones
        const nodes = activeTree?.allNodes ?? activeTree?.nodes;
        state.trees[state.active_tree_id].current_node = nodes.find((node) => node.id === lastNode.id);
    } else {
        const curIndex = activeTree.nodes.findIndex((n) => n.id === activeTree.current_node.id);
        if (curIndex > 0) {
            state.trees[state.active_tree_id].current_node = activeTree.nodes[curIndex - 1];
        }
    }
};

const findDependency = (dependencies, assetId) => {
    let asset = dependencies.assets.find((asset) => asset.asset_id == assetId);
    if (asset) {
        return asset;
    } else {
        for (const key of Object.keys(dependencies.assets)) {
            let depAsset = dependencies.assets[key];
            let asset = findDependency(depAsset.dependencies, assetId);
            if (asset) {
                return asset;
            }
        }
    }
    return null;
};

const fillBranchNodes = ({ dependencies, branchesAdded, parentProcedure, nodes, procedureIdsLoaded }) => {
    if (!Array.isArray(nodes) || nodes?.length === 0 || procedureIdsLoaded.has(parentProcedure.procedure_id)) {
        if (procedureIdsLoaded.has(parentProcedure.procedure_id)) {
            console.log(`BRANCH: Warning: recursive reference to ${parentProcedure.procedure_id}`);
        }
        return branchesAdded;
    }
    for (var node of nodes) {
        if (node.type === 'branch') {
            if (node.asset_id !== 0) {
                const branchAsset = findDependency(dependencies, node.asset_id);
                if (!branchAsset) {
                    console.log(
                        `BRANCH: Procedure ${parentProcedure.procedure_id} could not find asset ${node.asset_id} in dependencies`
                    );
                }
                if (branchAsset && Number(branchAsset.asset_type_id) === 3) {
                    branchesAdded++;
                    var procedure = JSON.parse(branchAsset.procedure_json);
                    procedure.procedure_id = branchAsset.procedure_id;
                    procedure.parent_tree_id = parentProcedure.procedure_id;
                    node.asset_title = procedure.title ?? node.title;
                    node.branch_procedure = procedure;
                    procedureIdsLoaded.add(parentProcedure.procedure_id);
                    // Recursively get branch nodes in current tree
                    branchesAdded = fillBranchNodes({
                        dependencies: dependencies,
                        branchesAdded: branchesAdded,
                        parentProcedure: procedure,
                        nodes: procedure.nodes,
                        procedureIdsLoaded: procedureIdsLoaded,
                    });
                }
            }
        }
    }
    return branchesAdded;
};

const procedureSlice = createSlice({
    name: 'procedure',
    initialState,
    reducers: {
        setInitialState: (state, action) => {
            return initialState;
        },
        setActiveTreeData: (state, action) => {
            state.trees[state.active_tree_id] = state.trees?.[state.active_tree_id]
                ? { ...state.trees[state.active_tree_id], ...action.payload }
                : state.trees[state.active_tree_id];
        },
        getNewNode: (state, action) => {
            const nodes = state.trees[state.active_tree_id].nodes;
            const lastNode = last(nodes);
            const newNode = {
                id: Number(lastNode.id) + 1,
                type: ProcedureNodeTypes.content.type,
                order_idx: lastNode + 1,
                back_button: false,
                next_button: false,
                submit_button: false,
                option_buttons: [],
                title: '',
                question: '',
                content: '',
                fields: [],
                target_title: '',
                target_details: '',
                target_url: '',
            };
            return newNode;
        },
        setActiveProcedureNodes: (state, action) => {
            if (action.payload?.nodes) {
                const activeTree = state.trees[state.active_tree_id];
                const nodes = action.payload.nodes;
                const allNodes = action.payload.allNodes ?? nodes;

                activeTree.allNodes = allNodes;
                activeTree.nodes = nodes;

                if (action.payload?.start_node_id) {
                    let currentNode = nodes.find((node) => Number(node.id) === Number(action.payload.start_node_id));
                    activeTree.current_node = currentNode;
                    activeTree.start_node_id = currentNode.id;
                }
                if (action.payload?.clearHistory) {
                    activeTree.history = [];
                }

                const currentNode = activeTree.current_node;
                if (currentNode?.answer_group === 'hotspot') {
                    const currentNodeExistsInFilteredNodes = nodes.some(
                        (node) => Number(node.id) === Number(currentNode.id)
                    );
                    if (currentNodeExistsInFilteredNodes === false) {
                        return;
                    }
                }

                const currentNodeExistsInAllNodes =
                    currentNode &&
                    (allNodes || []).some((node) => Number(node.id) === Number(currentNode.id));
                const currentNodeExistsInFilteredNodes =
                    currentNode && nodes.some((node) => Number(node.id) === Number(currentNode.id));

                if (
                    !currentNode ||
                    (nodes.length > 0 &&
                        currentNode.type === ProcedureNodeTypes.content.type &&
                        currentNodeExistsInFilteredNodes === false &&
                        currentNodeExistsInAllNodes === false)
                ) {
                    activeTree.current_node = nodes[0] ?? null;
                }
            }
        },
        setProcedureState: (state, action) => {
            state.trees = {};
            state.trees[action.payload.procedure_id] = convertNodeTypes({
                ...action.payload,
            });
            var activeTree = state.trees[action.payload.procedure_id];
            if (activeTree.nodes && activeTree.nodes?.length > 0) {
                if (action.payload.dependencies) {
                    var branchesAdded = 0;
                    const procedureIdsLoaded = new Set();
                    fillBranchNodes({
                        dependencies: action.payload.dependencies,
                        branchesAdded: branchesAdded,
                        parentProcedure: activeTree,
                        nodes: activeTree.nodes,
                        procedureIdsLoaded: procedureIdsLoaded,
                    });
                }

                state.trees[action.payload.procedure_id].nodes = sortNodes(activeTree.nodes);
                state.trees[action.payload.procedure_id].primary_tag_variable = action.payload?.primary_tag_variable;
                state.trees[action.payload.procedure_id].secondary_tag_variable =
                    action.payload?.secondary_tag_variable;
                state.trees[action.payload.procedure_id].save_sessions = action.payload?.save_sessions;
                setInitialNode(activeTree, action.payload.start_node_id);
                if (action.payload?.clearHistory) {
                    activeTree.history = [];
                } else {
                    activeTree.history = action.payload?.history ?? [];
                }
                state.active_tree_id = activeTree.procedure_id;
                return state;
            }
        },
        updateNodes: (state, action) => {
            state.trees[state.active_tree_id].nodes = action.payload;
        },
        setDisplaySessionDialog: (state, action) => {
            state.displaySessionDialog = action.payload;
        },
        updateNode: (state, action) => {
            const activeTree = state.trees[state.active_tree_id];
            const nodeIdx = activeTree.nodes.findIndex((element) => element.id === action.payload.id);
            if (nodeIdx !== -1) {
                activeTree.nodes[nodeIdx] = action.payload;
            }
            state.trees[state.active_tree_id] = activeTree;
        },
        setActiveTreeId: (state, action) => {
            state.active_tree_id = action.payload;
        },
        clearCurrentNode: (state, action) => {
            const activeTree = state.trees[state.active_tree_id];
            if (activeTree) {
                activeTree.current_node = null;
            }
        },
        resetCurrentTree: (state, action) => {
            const activeTree = state.trees[state.active_tree_id];
            if (activeTree) {
                setInitialNode(activeTree);
                activeTree.history = [];
                delete activeTree.parent_tree_id;
            }
        },
        setParentTreeId: (state, action) => {
            state.trees[state.active_tree_id].parent_tree_id = action.payload;
        },
        previousNode: (state, action) => {
            doPreviousNode(state);
        },
        gotoNode: (state, action) => {
            doGotoNode(state, action.payload);
        },
        nextNode: (state, action) => {
            doNextNode(state);
        },
        handleNodeAction: (state, action) => {
            const payloadAction = action.payload.action;
            const addToHistory = action.payload.addToHistory;
            const activeTree = state.trees[state.active_tree_id];
            switch (payloadAction.id) {
                case 'next':
                    doNextNode(state);
                    break;
                case 'previous':
                    doPreviousNode(state);
                    break;
                default:
                    if (typeof payloadAction.id === 'number') {
                        doGotoNode(state, payloadAction.id, addToHistory);
                    } else {
                        // Make sure we don't end on a non-display node
                        const activeTree = state.trees[state.active_tree_id];
                        if (
                            ProcedureNodeTypes[activeTree.current_node.type].mode !== 'display' &&
                            activeTree.lastDisplayNode
                        ) {
                            doGotoNode(state, activeTree.lastDisplayNode.id);
                        }
                    }
                    break;
            }
        },
        setProcedureAnswer: (state, action) => {
            const activeTree = state.trees[state.active_tree_id];
            activeTree.current_node.history_answer =
                action.payload.answer.answer_value ?? action.payload.answer.button_text;
            const nodeIdx = activeTree.nodes.findIndex((element) => element.id === action.payload.id);
            if (nodeIdx !== -1) {
                activeTree.nodes[nodeIdx].answer = action.payload.answer;
            }
        },
        unsetProcedureAnswer: (state, action) => {
            const activeTree = state.trees[state.active_tree_id];
            const nodeIdx = activeTree.nodes.findIndex((element) => element.id === action.payload.id);
            if (nodeIdx !== -1) {
                activeTree.nodes[nodeIdx].answer = null;
            }
        },
        setIsBranchNode: (state, action) => {
            state.isBranchProcedure = action.payload;
        },
        setHotspotRetryCount: (state, _) => {
            const prev = state.hotspotRetryCount + 1;
            state.hotspotRetryCount = prev;
        },
        resetHotspotCount: (state, _) => {
            state.hotspotRetryCount = 0;
        },
        setCurrentNode: (state, action) => {
            const node_id = action.payload;
            const activeTreeId = state.active_tree_id;

            if (!activeTreeId) return;

            const activeTree = state.trees[activeTreeId];
            if (!activeTree) return;

            const nextNode = findNodeById(activeTree, node_id);
            if (!nextNode) return;

            activeTree.current_node = nextNode;
        },
        restoreProcedureState: (_, action) => {
            return action.payload;
        },
        setNodeInitialView: (state, action) => {
            // payload: { node_id, initial_view }
            const { node_id, initial_view } = action.payload || {};
            const tree = state.trees[state.active_tree_id];
            if (!tree || !Array.isArray(tree.nodes)) return;

            const targetId = node_id ?? tree.current_node?.id ?? tree.current_node_id ?? null;
            if (!targetId) return;

            const idx = tree.nodes.findIndex((n) => Number(n.id) === Number(targetId));
            if (idx === -1) return;

            // update node in tree.nodes
            tree.nodes[idx] = {
                ...tree.nodes[idx],
                initial_view: initial_view,
            };

            // also update any current_node reference
            if (tree.current_node && Number(tree.current_node.id) === Number(targetId)) {
                tree.current_node = {
                    ...tree.current_node,
                    initial_view: initial_view,
                };
            }

            // if state has a top-level cache of nodes, try to update there as well
            if (state.trees && state.trees[state.active_tree_id]) {
                const activeTree = state.trees[state.active_tree_id];
                const idx = activeTree.nodes.findIndex((n) => Number(n.id) === Number(targetId));
                if (idx !== -1) {
                    activeTree.nodes[idx] = {
                        ...activeTree.nodes[idx],
                        initial_view: initial_view,
                    };
                }
            }
        },
        setNodeViewState: (state, action) => {
            const { node_id, view_state } = action.payload || {};
            const activeTree = state.trees[state.active_tree_id];
            if (!activeTree || node_id == null || !view_state) return;

            const updateNode = (node) =>
                node && Number(node.id) === Number(node_id) ? { ...node, view_state } : node;

            activeTree.nodes = activeTree.nodes?.map(updateNode);
            activeTree.allNodes = activeTree.allNodes?.map(updateNode);
            activeTree.history = activeTree.history?.map(updateNode);
            activeTree.current_node = updateNode(activeTree.current_node);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProcedure.pending, (state, action) => {
                state.loadingStatus = LoadingStatus.Loading;
            })
            .addCase(fetchProcedure.fulfilled, (state, action) => {
                state.loadingStatus = LoadingStatus.Loaded;
                let procedure = action.payload.procedure;
                procedure.procedure_id = action.payload.procedure_id;
                procedure = convertNodeTypes(procedure);
                state.trees = {};
                state.trees[action.payload.procedure_id] = { ...procedure };
                var activeTree = state.trees[action.payload.procedure_id];
                if (activeTree.nodes && activeTree.nodes?.length > 0) {
                    if (action.meta?.arg?.start_node_id) {
                        activeTree.start_node_id = action.meta?.arg?.start_node_id;
                    }
                    state.trees[action.payload.procedure_id].nodes = sortNodes(activeTree.nodes);
                    activeTree.history = [];
                    setInitialNode(activeTree);
                }
                state.active_tree_id = activeTree.procedure_id;
            })
            .addCase(fetchProcedure.rejected, (state, action) => {
                state.loadingStatus = LoadingStatus.Failed;
                state.error = action.error.message;
            });
    },
});

export const getCurrentNode = (state) => {
    return state.procedure.trees[state.procedure.active_tree_id]?.current_node;
};

export const getActiveTreeId = (state) => state.procedure.active_tree_id;

export const getActiveTree = (state) => state.procedure.trees[state.procedure.active_tree_id];

export const getLoadingStatus = (state) => state.procedure.loadingStatus;
export const getDisplaySessionDialog = (state) => state.procedure.displaySessionDialog;

export const getBranchNode = (state) => state.procedure.isBranchProcedure;

export const getHotspotRetryCount = (state) => state.procedure.hotspotRetryCount;

export const {
    setInitialState,
    setActiveTreeData,
    updateNodes,
    updateNode,
    setProcedureState,
    handleNodeAction,
    nextNode,
    gotoNode,
    previousNode,
    getCurrentNodeData,
    setProcedureAnswer,
    unsetProcedureAnswer,
    setActiveTreeId,
    setParentTreeId,
    resetCurrentTree,
    clearCurrentNode,
    getNewNode,
    setDisplaySessionDialog,
    setActiveProcedureNodes,
    setIsBranchNode,
    setHotspotRetryCount,
    resetHotspotCount,
    setCurrentNode,
    restoreProcedureState,
    setNodeInitialView,
    setNodeViewState,
} = procedureSlice.actions;

export default procedureSlice.reducer;
